import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconInfoCircle } from '@tabler/icons-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@core-ui/Components/ui/tooltip';
import EmptyState from '../shared/EmptyState';
import { sk } from '../shared/skeletonClass';
import type { FunnelStage } from '@types';

interface PipelineFunnelChartProps {
  data: FunnelStage[];
  isLoading: boolean;
}

const FUNNEL_COLORS = [
  'bg-primary',
  'bg-primary/80',
  'bg-primary/60',
  'bg-primary/50',
  'bg-primary/40',
  'bg-primary/30',
  'bg-emerald-500',
];

export default function PipelineFunnelChart ({ data, isLoading }: PipelineFunnelChartProps): React.JSX.Element {
  const { t } = useTranslation();
  const maxCount = data.length > 0 ? data[0].count : 1;

  // Check if there's any actual activity (contacted > 0)
  const hasActivity = data.length > 1 && data[1].count > 0;

  const translateStage = (stage: string): string => {
    const key = `dashboard.overview.funnel.stages.${stage}`;
    const translated = t(key);
    return translated !== key ? translated : stage;
  };

  // Returns a definition string when the stage's meaning needs a hint, or null
  // when the label is self-explanatory.
  const getStageTooltip = (stage: string): string | null => {
    const key = `dashboard.overview.funnel.tooltips.${stage}`;
    const translated = t(key);
    return translated !== key ? translated : null;
  };

  // Calculate stage-to-stage conversion rates
  const conversions = data.slice(0, -1).map((stage, i) => {
    const next = data[i + 1];
    const rate = stage.count > 0 ? ((next.count / stage.count) * 100).toFixed(1) : '0';
    const fromLabel = translateStage(stage.stage).split(' ').pop() ?? '';
    const toLabel = translateStage(next.stage).split(' ').pop() ?? '';
    return { label: `${fromLabel}\u2192${toLabel}`, rate };
  });

  if (isLoading) {
    return (
      <div className="rounded-xl border border-border bg-card">
        <div className="p-5 pb-3">
          <div className={`h-5 w-32 mb-1.5 ${sk}`} />
          <div className={`h-3 w-56 ${sk}`} />
        </div>
        <div className="px-5 pb-5 space-y-4">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i}>
              <div className="flex items-center justify-between mb-1.5">
                <div className={`h-4 w-24 ${sk}`} />
                <div className={`h-4 w-16 ${sk}`} />
              </div>
              <div
                className={`h-2 rounded-full ${sk}`}
                style={{ width: `${100 - i * 13}%` }}
              />
            </div>
          ))}
          <div className={`h-px w-full mt-3 ${sk}`} />
          <div className="flex flex-wrap items-center justify-between gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="text-center">
                <div className={`h-3 w-20 mx-auto mb-1 ${sk}`} />
                <div className={`h-4 w-10 mx-auto ${sk}`} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="p-5 pb-3">
        <h3 className="text-base font-semibold text-foreground">{t('dashboard.overview.funnel.title')}</h3>
        <p className="text-sm text-muted-foreground">{t('dashboard.overview.funnel.description')}</p>
      </div>
      <div className="px-5 pb-5 max-h-[400px] overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb]:rounded-full">
        {(!hasActivity && data.length === 0) && (
          <EmptyState message={t('dashboard.overview.funnel.empty')} />
        )}
        {!hasActivity && data.length > 0 && (
          <EmptyState message={t('dashboard.overview.funnel.noContacts')} />
        )}
        {hasActivity && <>
          {/* Funnel bars */}
          <div className="space-y-3">
            {data.map((stage, index) => {
              const tooltip = getStageTooltip(stage.stage);

              return (
                <div key={stage.stage}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-foreground flex items-center gap-1.5">
                      {translateStage(stage.stage)}
                      {tooltip !== null && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              aria-label={tooltip}
                              className="text-muted-foreground hover:text-foreground transition-colors"
                            >
                              <IconInfoCircle size={14} />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">{tooltip}</TooltipContent>
                        </Tooltip>
                      )}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold tabular-nums text-foreground">
                        {stage.count.toLocaleString()}
                      </span>
                      <span className="text-xs text-muted-foreground tabular-nums">{stage.percentage}%</span>
                    </div>
                  </div>
                  <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${FUNNEL_COLORS[index] ?? 'bg-primary/40'}`}
                      style={{ width: `${(stage.count / maxCount) * 100}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Conversion rates */}
          <div className="flex flex-wrap items-center justify-between gap-2 mt-5 pt-4 border-t border-border">
            {conversions.map(({ label, rate }) => (
              <div key={label} className="text-center">
                <div className="text-[11px] text-muted-foreground">{label}</div>
                <div className="text-sm font-semibold text-primary tabular-nums">{rate}%</div>
              </div>
            ))}
          </div>
        </>}
      </div>
    </div>
  );
}
